import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { ChevronDown, ChevronRight, MinusCircle, Star } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import HeatmapSettingsPanel from '../../Screener/HeatmapSettingsPanel';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { formatScenarioLabel } from '../../../utils/elasticityCalculations';
import {
  DEFAULT_STRADDLE_SCENARIOS,
  DISPLAY_MULTIPLIER,
  DISPLAY_PNL,
  DISPLAY_RETURN,
  HORIZON_CHIPS,
  IV_SHIFT_CHIPS,
  MOVE_EARLY,
  MOVE_STEADY,
  PLANNED_MOVE_CHIPS,
  RANK_AVERAGE,
  RANK_BOTH_WAYS,
  RANK_BREAKEVEN,
  RANK_HIGHEST,
  STRADDLE_SCENARIO_OPTIONS,
  VIEW_CALL,
  VIEW_NET,
  VIEW_PUT,
  cellMetric,
  cellReturn,
  clearUserStraddleScenarios,
  computeStraddleCell,
  computeStraddleHeatmapRow,
  findStraddleBreakevens,
  firstExpiryDays,
  formatCell,
  formatDollars,
  formatPrice,
  formatReturn,
  formatScore,
  getStraddleCellColor,
  getUserStraddleScenarios,
  hasModelGap,
  maxHorizonDays,
  rankStraddles,
  resolveHorizon,
  saveUserStraddleScenarios,
} from '../../../utils/straddleCalculations';
import {
  formatLegLabel,
  formatSignedPct,
  formatStrike,
  matchesFilters,
} from '../../../utils/straddleMappings';
import { StraddleCostCell } from './StraddleSizingControls';
import StraddleRowTooltip, { TIP_IV, TIP_QUALITY, rowTip } from './StraddleRowTooltip';

/**
 * Columns before the scenario grid. Contents are fitted to the fixed sticky slots
 * in ui.css: its left offsets don't move, so a wide value in a narrow slot would
 * overlap the next column when scrolled. Slot 4 is the wide one.
 */
const STICKY_COLUMNS = [
  { key: 'Ticker', label: 'Ticker', stickyIndex: 1 },
  { key: 'Structure', label: 'Type', stickyIndex: 2 },
  { key: 'Cost', label: 'Cost', stickyIndex: 3 },
  { key: 'Legs', label: 'Legs', stickyIndex: 4 },
  { key: 'Rank', label: 'Rank', stickyIndex: 5 },
  { key: 'Breakeven', label: 'Break-\neven', stickyIndex: 6 },
];

/** After the grid, so the scenario columns are on screen without scrolling. */
const TRAILING_COLUMNS = [
  { key: 'Underlying Price', label: 'Underlying' },
  { key: 'IV', label: 'IV' },
  { key: 'Quality', label: 'Liquidity' },
  { key: 'DaysToExit', label: 'Days to\nExit' },
];

const HEADER_TOOLTIPS = {
  Structure: 'Straddle: both legs on one strike. Strangle: two strikes. Below that, how the two expirations compare.',
  Cost: 'What this position costs in total, and the most it can lose. With a budget set, the second line is how many calls and puts it buys.',
  Legs: 'Each leg: strike, expiration, and days until it expires.',
  Rank: 'Position under the current Rank-by choice, with its score. Rows with unreliable quotes stay listed but unranked.',
  Breakeven: 'How far the stock must move up or down to break even at this horizon.',
  IV: 'Implied volatility per leg. Hover for the app and Massive values.',
  Quality: 'The worse leg decides: bid-ask spread, open interest and where the price came from.',
  DaysToExit: 'The horizon being simulated. Both legs are valued on the same calendar day.',
};

const QUALITY_ORDER = ['Good', 'Fair', 'Poor'];
const QUALITY_COLORS = { Good: '#10b981', Fair: '#f59e0b', Poor: '#ef4444' };

const CELL_VIEWS = [
  { key: VIEW_NET, label: 'Net' },
  { key: VIEW_CALL, label: 'Call' },
  { key: VIEW_PUT, label: 'Put' },
];

const DISPLAY_MODES = [
  { key: DISPLAY_RETURN, label: 'Return on Debit %', title: 'Projected profit or loss as a % of what was paid' },
  { key: DISPLAY_PNL, label: 'P&L $', title: 'Projected profit or loss in dollars, per contract' },
  { key: DISPLAY_MULTIPLIER, label: 'Multiplier', title: 'Return % for each 1% the stock moves, in either direction' },
];

const RANK_OPTIONS = [
  { key: RANK_BOTH_WAYS, label: 'Both ways', title: 'The worse of the returns at +move and -move: whichever way it goes, you make at least this' },
  { key: RANK_HIGHEST, label: 'Highest gain', title: 'The best % gain on the whole straddle across the scenario columns selected above' },
  { key: RANK_AVERAGE, label: 'Average', title: 'Average return across the scenario columns selected above' },
  { key: RANK_BREAKEVEN, label: 'Smallest breakeven', title: 'The move needed to break even in the harder direction' },
];

const MOVE_TIMINGS = [
  { key: MOVE_STEADY, label: 'Steady', title: 'The stock drifts in a straight line to each column\'s price' },
  { key: MOVE_EARLY, label: 'Early', title: 'The whole move lands before a short-dated leg expires' },
];

const MUTED_CELL = { color: '#6b7280', backgroundColor: 'transparent', opacity: 0.7 };

const tooltipStyle = {
  backgroundColor: '#111827', color: '#fff', borderRadius: 8, padding: '8px 12px',
  lineHeight: 1.5, zIndex: 1200, boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid #374151',
};

/** MobileScreener.css gives every button a 44px minimum, too big for this slot. */
const ROW_ACTION_SIZE = { minWidth: 16, minHeight: 16, padding: 0 };

const scenarioLabel = (pct) => (pct === 0
  ? 'Time decay only: the stock is flat at this horizon'
  : `Projected result if the stock ${pct > 0 ? 'rises' : 'drops'} ${Math.abs(pct)}% by this horizon`);

const ROW_TIP_ID = 'straddle-row-tooltip';
const CELL_TIP_ID = 'straddle-cell-tooltip';

function TipLine({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const signedDollars = (value) => `${value > 0 ? '+' : ''}${formatDollars(value)}`;

function LegTipLine({ name, strike, leg, sized }) {
  const held = sized ? ` x${leg.contracts}` : '';
  if (leg.settled) {
    return (
      <TipLine
        label={`${name}${held} settled day ${Math.round(leg.settledDay)} at ${formatPrice(leg.stock)}`}
        value={`${formatPrice(leg.value)} (${signedDollars(leg.pnl)})`}
      />
    );
  }
  return (
    <TipLine
      label={`${name} ${formatStrike(strike)}${held}`}
      value={`${formatPrice(leg.premium)} → ${formatPrice(leg.value)} (${signedDollars(leg.pnl)})`}
    />
  );
}

/**
 * What a scenario cell is made of. Built on hover, never ahead of time: formatting
 * this for every cell was 68% of the cost of a slider move, for text nobody reads.
 */
function CellTip({ row, cell, breakevens }) {
  const breakeven = breakevens?.alwaysProfitable
    ? 'None: profitable at every price'
    : `${breakevens?.low != null ? formatPrice(breakevens.low) : 'None'} / ${breakevens?.high != null ? formatPrice(breakevens.high) : 'None'}`;
  // Each leg line is per share. Net is whole dollars once a budget sets the counts,
  // since a per-share sum across different counts wouldn't mean anything.
  const sized = !!row._size;
  const netFrom = sized ? formatDollars(row.Cost) : formatPrice(row['Net Debit']);
  const netTo = sized ? formatDollars(cell.value * 100) : formatPrice(cell.value);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TipLine label="Underlying" value={`${formatPrice(row['Underlying Price'])} → ${formatPrice(cell.horizonPrice)}`} />
      <LegTipLine name="Call" strike={row['Call Strike']} leg={cell.call} sized={sized} />
      <LegTipLine name="Put" strike={row['Put Strike']} leg={cell.put} sized={sized} />
      <TipLine label="Net" value={`${netFrom} → ${netTo} (${signedDollars(cell.pnl)}, ${formatReturn(cell.returnPct)})`} />
      <div style={{ borderTop: '1px solid #374151', paddingTop: 2, marginTop: 1 }}>
        <TipLine label="Breakeven" value={breakeven} />
      </div>
    </div>
  );
}

/** A row of one-choice buttons with a label in front. */
function ChipGroup({ label, options, value, onChange, isDisabled }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-gray-300">{label}</span>
      {options.map((option) => {
        const disabled = isDisabled ? isDisabled(option.key) : false;
        const selected = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            disabled={disabled}
            aria-pressed={selected}
            title={option.title}
            className={`tab-button ${selected ? 'tab-selected' : 'tab-unselected'}${disabled ? ' opacity-40 cursor-not-allowed' : ''}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const cellClassFor = (index, isMobile) => (index && !(isMobile && index > 1)
  ? `table-cell-sticky table-cell-sticky-${index}`
  : 'table-cell');

function renderScenarioCell(row, cell, view, pct, displayMode, day) {
  const value = cellMetric(cell, view, displayMode);
  const muted = cell?.fullySettled;
  const legSettled = cell && (
    view === VIEW_CALL ? cell.call.settled
      : view === VIEW_PUT ? cell.put.settled
        : cell.call.settled || cell.put.settled
  );
  return (
    <td
      key={pct}
      className="table-cell text-center font-medium whitespace-nowrap"
      style={{
        ...(muted ? MUTED_CELL : getStraddleCellColor(cellReturn(cell, view))),
        minWidth: 62,
        paddingLeft: 6,
        paddingRight: 6,
        cursor: 'help',
      }}
      data-tooltip-id={CELL_TIP_ID}
      data-tip-row={row._key}
      data-tip-pct={pct}
      {...(day ? { 'data-tip-day': day } : {})}
    >
      {formatCell(value, displayMode)}
      {legSettled && <span style={{ fontSize: 9, marginLeft: 3, opacity: 0.8 }}>EXP</span>}
    </td>
  );
}

function renderEssential(row, key, rankBy) {
  switch (key) {
    case 'Ticker':
      return row.Ticker;
    case 'Structure':
      return (
        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
          <div>{row.Structure}</div>
          <div className="text-gray-400">{row['Expiry Relation']}</div>
        </div>
      );
    case 'Cost':
      return <StraddleCostCell row={row} tooltipId={ROW_TIP_ID} />;
    case 'Legs':
      return (
        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
          <div>{formatLegLabel(row, 'call')}</div>
          <div>{formatLegLabel(row, 'put')}</div>
        </div>
      );
    case 'Rank':
      if (row._rank === null) {
        return (
          <span
            className="text-gray-500"
            data-tooltip-id="straddle-info-tooltip"
            data-tooltip-content={row._unranked}
          >
            Unranked
          </span>
        );
      }
      return (
        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
          <div className="font-semibold">#{row._rank}</div>
          <div className="text-gray-300">{formatScore(row._score, rankBy)}</div>
        </div>
      );
    case 'Breakeven': {
      const be = row._breakevens;
      if (be?.alwaysProfitable) return <span className="text-gray-400" style={{ fontSize: 11 }}>None</span>;
      return (
        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
          <div>{formatSignedPct(be?.highPct)}</div>
          <div>{formatSignedPct(be?.lowPct)}</div>
        </div>
      );
    }
    case 'Underlying Price':
      return formatPrice(row['Underlying Price']);
    case 'IV':
      return (
        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
          {['call', 'put'].map((side) => {
            const leg = row._legs[side];
            return (
              <div
                key={side}
                {...rowTip(ROW_TIP_ID, row, TIP_IV, side)}
                style={leg.is_default_iv ? { color: '#f59e0b' } : undefined}
              >
                {side === 'call' ? 'C' : 'P'} {(leg.iv * 100).toFixed(0)}%
              </div>
            );
          })}
        </div>
      );
    case 'Quality':
      return (
        <div style={{ fontSize: 11, lineHeight: 1.3 }} {...rowTip(ROW_TIP_ID, row, TIP_QUALITY)}>
          <div style={{ color: QUALITY_COLORS[row.Quality] }}>{row.Quality}</div>
          {row._modelGap && <div style={{ color: '#f59e0b' }}>Model gap</div>}
        </div>
      );
    case 'DaysToExit':
      return row._horizon;
    default:
      return row[key] ?? '-';
  }
}

/**
 * One straddle row. Memoized so moving the slider skips the whole table on its
 * urgent pass, and as separate components so the deferred pass that follows can
 * yield between rows instead of freezing the page.
 */
const StraddleRow = React.memo(function StraddleRow({
  row, stripe, isFavorited, isExpanded, cellView, displayMode, scenarioArray, rankBy, isMobile,
  pricingOptions, onToggleFavorite, onRemove, onToggleExpanded,
}) {
  return (
    <>
      <tr className={`${stripe}${isFavorited ? ' table-row-favorited' : ''}`}>
        <td className="table-cell-sticky table-cell-sticky-0" style={{ padding: '2px' }}>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => onToggleFavorite(row._key)}
              className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20 flex items-center justify-center transition-all duration-200 group"
              style={ROW_ACTION_SIZE}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star
                className={`w-3 h-3 transition-colors duration-200 ${isFavorited ? 'text-yellow-400' : 'text-gray-400 group-hover:text-yellow-400'}`}
                fill={isFavorited ? 'currentColor' : 'none'}
              />
            </button>
            <button
              type="button"
              onClick={() => onRemove(row._key)}
              className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20 flex items-center justify-center transition-all duration-200 group"
              style={ROW_ACTION_SIZE}
              title="Remove from results"
            >
              <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
            </button>
            <button
              type="button"
              onClick={() => onToggleExpanded(row._key)}
              aria-expanded={isExpanded}
              className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#218aff] hover:bg-[#218aff]/20 flex items-center justify-center transition-all duration-200 group"
              style={ROW_ACTION_SIZE}
              title={isExpanded ? 'Hide each contract' : 'Show each contract at 10, 30 and 90 days'}
            >
              {isExpanded
                ? <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-[#218aff]" />
                : <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-[#218aff]" />}
            </button>
          </div>
        </td>

        {STICKY_COLUMNS.map((col) => (
          <td key={col.key} className={`${cellClassFor(col.stickyIndex, isMobile)} table-cell-number`}>
            {renderEssential(row, col.key, rankBy)}
          </td>
        ))}

        {scenarioArray.map((pct) => renderScenarioCell(row, row._heatmap?.[pct], cellView, pct, displayMode))}

        {TRAILING_COLUMNS.map((col) => (
          <td key={col.key} className="table-cell table-cell-number">
            {renderEssential(row, col.key, rankBy)}
          </td>
        ))}
      </tr>

      {/* Net, Call and Put lines at each comparison day, aligned under the scenario columns. */}
      {isExpanded && HORIZON_CHIPS.flatMap((day) => CELL_VIEWS.map((view, viewIndex) => (
        <tr
          key={`${row._key}|${day}|${view.key}`}
          className={stripe}
          style={viewIndex === 0 ? { borderTop: '1px solid #374151' } : undefined}
        >
          <td className="table-cell-sticky table-cell-sticky-0" />
          {STICKY_COLUMNS.map((col) => (
            <td key={col.key} className={cellClassFor(col.stickyIndex, isMobile)}>
              {col.key === 'Legs' && (
                <span className="text-xs text-gray-300">
                  {viewIndex === 0 ? `Day ${day}: ` : ''}{view.label}
                </span>
              )}
            </td>
          ))}
          {scenarioArray.map((pct) => renderScenarioCell(
            row,
            computeStraddleCell(row, day, pct, pricingOptions),
            view.key,
            pct,
            displayMode,
            day,
          ))}
          {TRAILING_COLUMNS.map((col) => <td key={col.key} className="table-cell" />)}
        </tr>
      )))}
    </>
  );
});

const HORIZON_OPTIONS = HORIZON_CHIPS.map((d) => ({ key: d, label: `${d}d` }));
const MOVE_OPTIONS = PLANNED_MOVE_CHIPS.map((m) => ({ key: m, label: `±${m}%` }));
const IV_OPTIONS = IV_SHIFT_CHIPS.map((s) => ({ key: s, label: `${s > 0 ? '+' : ''}${s}%` }));

/**
 * Elasticity heatmap for straddles.
 *
 * Rows are call + put pairs, columns are stock moves, and cells are what the pair
 * returns at the chosen horizon. Everything reprices here with no API call, and
 * each row expands to show both contracts at 10, 30 and 90 days.
 *
 * Mounted with key={runId}: a new run gets fresh state, so a slider value can't
 * carry over from the previous result set.
 */
function StraddleHeatmapView({
  results,
  favoritedRows, setFavoritedRows,
  removedRows, setRemovedRows,
  showFavoritesOnly, setShowFavoritesOnly,
  sharedDaysToExit, setSharedDaysToExit,
  daysToExitMode, setDaysToExitMode,
  daysToExitPercent, setDaysToExitPercent,
  defaultDays,
  filters,
  moveTiming, setMoveTiming,
  ivShiftPct, setIvShiftPct,
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [selectedScenarios, setSelectedScenariosRaw] = useState(() => getUserStraddleScenarios());
  const setSelectedScenarios = useCallback((next) => {
    if (next instanceof Set) saveUserStraddleScenarios(next);
    setSelectedScenariosRaw(next);
  }, []);

  const [displayMode, setDisplayMode] = useState(DISPLAY_RETURN);
  const [cellView, setCellView] = useState(VIEW_NET);
  const [rankBy, setRankBy] = useState(RANK_BOTH_WAYS);
  const [plannedMove, setPlannedMove] = useState(15);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [expandedRows, setExpandedRows] = useState(() => new Set());

  const scenarioArray = useMemo(
    () => Array.from(selectedScenarios).sort((a, b) => a - b),
    [selectedScenarios]
  );

  const pricingOptions = useMemo(
    () => ({ moveTiming, ivShift: ivShiftPct / 100 }),
    [moveTiming, ivShiftPct]
  );

  // Stars only matter here while filtering by them, so starring a row otherwise
  // doesn't reprice the whole table.
  const favoritesFilter = showFavoritesOnly ? favoritedRows : null;
  const filteredData = useMemo(() => {
    if (!Array.isArray(results)) return [];
    return results.filter((row) => (
      matchesFilters(row, filters)
      && !removedRows.has(row._key)
      && (!favoritesFilter || favoritesFilter.has(row._key))
    ));
  }, [results, filters, removedRows, favoritesFilter]);

  const maxDays = useMemo(
    () => maxHorizonDays(filteredData.length > 0 ? filteredData : results),
    [filteredData, results]
  );

  const isPercent = daysToExitMode === 'percent' && daysToExitPercent != null;

  // Held within maxDays so the panel's clamp effect never fires. If it did, it
  // would call back into handleDaysChange and flip percent mode to fixed.
  const sliderDays = Math.min(sharedDaysToExit ?? defaultDays, maxDays);
  const deferredDays = useDeferredValue(sliderDays);
  const deferredPercent = useDeferredValue(daysToExitPercent);

  const handleDaysChange = useCallback((days) => {
    setDaysToExitMode('fixed');
    setDaysToExitPercent(null);
    setSharedDaysToExit(days);
  }, [setDaysToExitMode, setDaysToExitPercent, setSharedDaysToExit]);

  const handlePercentChange = useCallback((pct) => {
    setDaysToExitMode('percent');
    setDaysToExitPercent(pct);
  }, [setDaysToExitMode, setDaysToExitPercent]);

  const handleReset = useCallback(() => {
    handleDaysChange(defaultDays);
    clearUserStraddleScenarios();
    setSelectedScenariosRaw(new Set(DEFAULT_STRADDLE_SCENARIOS));
  }, [handleDaysChange, defaultDays]);

  const pricedData = useMemo(() => filteredData.map((row) => {
    const horizon = resolveHorizon(
      row,
      isPercent ? null : deferredDays,
      isPercent ? deferredPercent : null,
    );
    return {
      ...row,
      _horizon: horizon,
      _heatmap: computeStraddleHeatmapRow(row, horizon, scenarioArray, pricingOptions),
      _breakevens: findStraddleBreakevens(row, horizon, pricingOptions),
      _modelGap: hasModelGap(row),
    };
  }), [filteredData, isPercent, deferredDays, deferredPercent, scenarioArray, pricingOptions]);

  const rankedData = useMemo(() => rankStraddles(pricedData, {
    enabled: !isPercent,
    rankBy,
    plannedMove,
    scenarios: scenarioArray,
    ...pricingOptions,
  }), [pricedData, isPercent, rankBy, plannedMove, scenarioArray, pricingOptions]);

  const unrankedCount = isPercent ? 0 : rankedData.filter((row) => row._rank === null).length;

  const sortValue = (row, key) => {
    const scenario = key.match(/^scenario_(-?\d+(?:\.\d+)?)$/);
    if (scenario) return cellMetric(row._heatmap?.[parseFloat(scenario[1])], cellView, displayMode);
    switch (key) {
      case 'Legs': return firstExpiryDays(row);
      case 'Rank': return row._rank;
      case 'Breakeven': {
        const { lowPct, highPct } = row._breakevens || {};
        return lowPct == null || highPct == null ? null : Math.max(Math.abs(lowPct), Math.abs(highPct));
      }
      case 'IV': return (row._legs.call.iv + row._legs.put.iv) / 2;
      case 'Quality': return QUALITY_ORDER.indexOf(row.Quality);
      case 'DaysToExit': return row._horizon;
      default: return row[key];
    }
  };

  const sortedData = useMemo(() => {
    // Ticker, then the first leg to expire, then strike: the same spine the
    // screener heatmap uses when there is nothing better to sort on.
    const spine = (a, b) => (
      String(a.Ticker).localeCompare(String(b.Ticker))
      || firstExpiryDays(a) - firstExpiryDays(b)
      || a['Call Strike'] - b['Call Strike']
      || a['Put Strike'] - b['Put Strike']
    );
    const compareNullsLast = (aVal, bVal, direction) => {
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      const result = !Number.isNaN(aNum) && !Number.isNaN(bNum)
        ? aNum - bNum
        : String(aVal).localeCompare(String(bVal));
      return direction === 'asc' ? result : -result;
    };

    return [...rankedData].sort((a, b) => {
      const favorite = (favoritedRows.has(a._key) ? 0 : 1) - (favoritedRows.has(b._key) ? 0 : 1);
      if (favorite !== 0) return favorite;
      const primary = sortConfig.key
        ? compareNullsLast(sortValue(a, sortConfig.key), sortValue(b, sortConfig.key), sortConfig.direction)
        : compareNullsLast(a._rank, b._rank, 'asc');
      return primary || spine(a, b);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankedData, sortConfig, favoritedRows, cellView, displayMode]);

  // Choosing how to rank asks for the ranked order, so a column sort left over
  // from earlier mustn't keep the table from visibly changing.
  const clearColumnSort = () => setSortConfig({ key: null, direction: 'asc' });
  const handleRankByChange = (key) => {
    setRankBy(key);
    clearColumnSort();
  };
  const handlePlannedMoveChange = (move) => {
    setPlannedMove(move);
    clearColumnSort();
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key === column) {
        return { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // A scenario column's first click shows the best returns at that move.
      return { key: column, direction: column.startsWith('scenario_') ? 'desc' : 'asc' };
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  const toggleFavorite = useCallback((key) => {
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [setFavoritedRows]);

  const removeRow = useCallback((key) => {
    setRemovedRows((prev) => new Set([...prev, key]));
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, [setRemovedRows, setFavoritedRows]);

  const toggleExpanded = useCallback((key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const headerClass = (index) => (index && !(isMobile && index > 1)
    ? `table-header-cell-sticky table-header-cell-sticky-${index}`
    : 'table-header-cell');

  const metricLabel = useMemo(() => {
    const view = CELL_VIEWS.find((v) => v.key === cellView)?.label;
    return `${view} ${DISPLAY_MODES.find((d) => d.key === displayMode)?.label}`;
  }, [cellView, displayMode]);

  const csvData = useMemo(() => sortedData.map((row) => {
    const out = {
      Ticker: row.Ticker,
      Structure: row.Structure,
      'Expiry Relation': row['Expiry Relation'],
      'Call Strike': row['Call Strike'],
      'Call Expiration': row['Call Expiration'],
      'Call DTE': row['Call DTE'],
      'Put Strike': row['Put Strike'],
      'Put Expiration': row['Put Expiration'],
      'Put DTE': row['Put DTE'],
      Split: row.Split ?? '',
      'Call Contracts': row._size?.call.contracts ?? 1,
      'Put Contracts': row._size?.put.contracts ?? 1,
      Cost: row.Cost,
      Budget: row._size?.budget ?? '',
      'Underlying Price': row['Underlying Price'],
      'Breakeven Low': row._breakevens?.low?.toFixed(2) ?? '',
      'Breakeven High': row._breakevens?.high?.toFixed(2) ?? '',
      Rank: row._rank ?? '',
      Score: row._score?.toFixed(2) ?? '',
      Liquidity: row.Quality,
      'Days to Exit': row._horizon,
    };
    scenarioArray.forEach((pct) => {
      const value = cellMetric(row._heatmap?.[pct], cellView, displayMode);
      out[`${metricLabel} ${formatScenarioLabel(pct)}`] = value != null ? Number(value).toFixed(2) : '';
    });
    return out;
  }), [sortedData, scenarioArray, cellView, displayMode, metricLabel]);

  const rowsByKey = useMemo(() => new Map(sortedData.map((row) => [row._key, row])), [sortedData]);

  const renderCellTip = ({ activeAnchor }) => {
    const row = activeAnchor && rowsByKey.get(activeAnchor.getAttribute('data-tip-row'));
    if (!row) return null;
    const pct = Number(activeAnchor.getAttribute('data-tip-pct'));
    const day = activeAnchor.getAttribute('data-tip-day');
    // A cell in an expanded row is at its own comparison day, not the slider's.
    const cell = day
      ? computeStraddleCell(row, Number(day), pct, pricingOptions)
      : row._heatmap?.[pct];
    if (!cell) return null;
    const breakevens = day ? findStraddleBreakevens(row, Number(day), pricingOptions) : row._breakevens;
    return <CellTip row={row} cell={cell} breakevens={breakevens} />;
  };

  const renderColumnHeader = (col) => {
    const tip = HEADER_TOOLTIPS[col.key];
    return (
      <th
        key={col.key}
        onClick={() => handleSort(col.key)}
        className={headerClass(col.stickyIndex)}
        {...(tip ? { 'data-tooltip-id': 'straddle-info-tooltip', 'data-tooltip-content': tip } : {})}
      >
        <div className="flex flex-col items-center justify-center">
          <span className="text-center whitespace-pre-line">{col.label}</span>
          <span className="mt-1">{getSortIcon(col.key)}</span>
        </div>
      </th>
    );
  };

  if (!Array.isArray(results) || results.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="mb-2 flex flex-wrap items-center gap-4">
        <ChipGroup
          label="Horizon"
          options={HORIZON_OPTIONS}
          value={!isPercent ? sliderDays : null}
          onChange={handleDaysChange}
          isDisabled={(days) => days > maxDays}
        />
      </div>

      <HeatmapSettingsPanel
        maxDays={maxDays}
        maxExpirationDate={null}
        daysToExit={sliderDays}
        onDaysChange={handleDaysChange}
        selectedScenarios={selectedScenarios}
        onScenariosChange={setSelectedScenarios}
        scenarioOptions={STRADDLE_SCENARIO_OPTIONS}
        defaultScenarioSet={DEFAULT_STRADDLE_SCENARIOS}
        contractCount={sortedData.length}
        defaultExpanded
        defaultDays={Math.min(defaultDays, maxDays)}
        onReset={handleReset}
        isOverridden={sharedDaysToExit !== null}
        daysToExitMode={daysToExitMode}
        onModeChange={setDaysToExitMode}
        daysToExitPercent={daysToExitPercent}
        onPercentChange={handlePercentChange}
      />

      {/* Assumptions behind every cell */}
      <div className="bg-gray-800 rounded-lg px-4 py-3 mb-4 space-y-3">
        <ChipGroup
          label="Move timing"
          options={MOVE_TIMINGS}
          value={moveTiming}
          onChange={setMoveTiming}
        />
        <p className="text-xs text-gray-400 text-left">
          {moveTiming === MOVE_STEADY
            ? 'The stock drifts in a straight line to each column\'s price, so a leg that expires first settles at the price reached by then. This understates a leg that pays on a sudden move.'
            : 'The whole move lands before any leg expires, so each settles at the column\'s price. Useful for a known catalyst, but it flatters short-dated legs at long horizons.'}
        </p>
        <ChipGroup
          label="IV at exit"
          options={IV_OPTIONS}
          value={ivShiftPct}
          onChange={setIvShiftPct}
        />
        <p className="text-xs text-gray-400 text-left">
          Changes IV by this much at the horizon, on legs still alive. A long straddle can lose
          money on a correct move if IV falls, such as after earnings.
        </p>
        {isPercent && (
          <p className="text-xs text-gray-400 text-left">
            In % of Exp mode, 100% means exiting on the day the first leg expires.
          </p>
        )}
      </div>

      {/* Finding the best */}
      <div className="bg-gray-800 rounded-lg px-4 py-3 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <ChipGroup label="Rank by" options={RANK_OPTIONS} value={rankBy} onChange={handleRankByChange} />
          {rankBy === RANK_BOTH_WAYS && (
            <ChipGroup
              label="Planned move"
              options={MOVE_OPTIONS}
              value={plannedMove}
              onChange={handlePlannedMoveChange}
            />
          )}
        </div>
        <p className="text-xs text-gray-400 text-left">
          {isPercent
            ? 'Ranking needs one shared horizon, so it is off in % of Exp mode. Switch to Fixed Days to rank.'
            : 'The score depends on the move and horizon you choose and is not a probability. Entry is at the mark, with no slippage.'}
          {!isPercent && rankBy === RANK_HIGHEST
            && ' Highest gain usually comes from the largest column selected, which favors cheap, far-out strikes. To compare at one move, click that column\'s header.'}
          {unrankedCount > 0 && ` ${unrankedCount} of ${rankedData.length} rows are unranked: hover "Unranked" to see why.`}
        </p>
      </div>

      {/* Control bar */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <div className="flex flex-wrap gap-4 items-center">
          <ChipGroup label="Show" options={DISPLAY_MODES} value={displayMode} onChange={setDisplayMode} />
          <ChipGroup label="For" options={CELL_VIEWS} value={cellView} onChange={setCellView} />
          {sortConfig.key && (
            <button
              type="button"
              onClick={clearColumnSort}
              className="flex items-center gap-1 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-[#218aff]/10 hover:text-[#218aff] hover:border-[#218aff]"
              title="Back to the ranked order"
            >
              ↺ Ranked Order
            </button>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setShowFavoritesOnly((p) => !p)}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>
          <CSVLink data={csvData} filename="straddle_simulator_optionsLeveling.csv" className="btn-export-primary">
            Export CSV
          </CSVLink>
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {STICKY_COLUMNS.map(renderColumnHeader)}
                {scenarioArray.map((pct) => (
                  <th
                    key={pct}
                    onClick={() => handleSort(`scenario_${pct}`)}
                    className="table-header-cell"
                    style={{ minWidth: 62 }}
                    data-tooltip-id="straddle-info-tooltip"
                    data-tooltip-content={scenarioLabel(pct)}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-center whitespace-nowrap">{formatScenarioLabel(pct)}</span>
                      <span className="mt-1">{getSortIcon(`scenario_${pct}`)}</span>
                    </div>
                  </th>
                ))}
                {TRAILING_COLUMNS.map(renderColumnHeader)}
              </tr>
            </thead>

            <tbody>
              {sortedData.map((row, idx) => (
                <StraddleRow
                  key={row._key}
                  row={row}
                  stripe={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}
                  isFavorited={favoritedRows.has(row._key)}
                  isExpanded={expandedRows.has(row._key)}
                  cellView={cellView}
                  displayMode={displayMode}
                  scenarioArray={scenarioArray}
                  rankBy={rankBy}
                  isMobile={isMobile}
                  pricingOptions={pricingOptions}
                  onToggleFavorite={toggleFavorite}
                  onRemove={removeRow}
                  onToggleExpanded={toggleExpanded}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap justify-between items-center text-sm text-gray-400 mt-3 gap-2">
        <div>Showing {sortedData.length} of {results.length} {results[0]?._size?.showSplit ? 'rows' : 'straddles'}</div>
        <div className="text-xs">
          Cells show {metricLabel}. EXP marks a leg that has already expired by this horizon.
        </div>
      </div>

      <ReactTooltip
        id="straddle-info-tooltip"
        place="top"
        delayShow={300}
        style={{ ...tooltipStyle, fontSize: 13, maxWidth: 320, whiteSpace: 'pre-line' }}
      />
      <ReactTooltip
        id={CELL_TIP_ID}
        place="top"
        delayShow={200}
        style={{ ...tooltipStyle, fontSize: 12, maxWidth: 420 }}
        render={renderCellTip}
      />
      <StraddleRowTooltip
        id={ROW_TIP_ID}
        rowsByKey={rowsByKey}
        style={{ ...tooltipStyle, fontSize: 13, maxWidth: 320, whiteSpace: 'pre-line' }}
      />
    </div>
  );
}

export default StraddleHeatmapView;
